import { useEffect, useRef, useState } from "react";
import { useScroll } from "framer-motion";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "@/data/india-topo.json";
import AMark from "@/components/AMark";
import { MousePointer2, ChevronDown, Anchor } from "lucide-react";

// ---- Geometry (module-level, computed once) ----
const W = 1200;
const H = 1360;
const geo = feature(topo, topo.objects.india);
const projection = geoMercator().fitExtent([[40, 40], [W - 40, H - 40]], geo);
const pathGen = geoPath(projection);
const STATE_PATHS = geo.features.map((f, i) => ({ d: pathGen(f), key: i }));

// Major ports of India, ordered EAST -> WEST along the coastline.
const PORTS = [
  { name: "Kolkata / Haldia", region: "West Bengal", lon: 88.06, lat: 22.03 },
  { name: "Paradip", region: "Odisha", lon: 86.61, lat: 20.26 },
  { name: "Visakhapatnam", region: "Andhra Pradesh", lon: 83.28, lat: 17.69 },
  { name: "Chennai", region: "Tamil Nadu", lon: 80.29, lat: 13.1 },
  { name: "Tuticorin (V.O.C.)", region: "Tamil Nadu", lon: 78.13, lat: 8.76 },
  { name: "Kochi", region: "Kerala", lon: 76.26, lat: 9.97 },
  { name: "New Mangalore", region: "Karnataka", lon: 74.81, lat: 12.92 },
  { name: "Mormugao", region: "Goa", lon: 73.8, lat: 15.4 },
  { name: "Mumbai (JNPT)", region: "Maharashtra", lon: 72.95, lat: 18.95 },
  { name: "Kandla (Deendayal)", region: "Gujarat", lon: 70.22, lat: 22.03 },
];
const PTS = PORTS.map((p) => {
  const [x, y] = projection([p.lon, p.lat]);
  return { ...p, x, y };
});
const FLIGHT_D = "M " + PTS.map((p) => `${p.x} ${p.y}`).join(" L ");
const ES = W * 0.05; // eagle size in map units

function sample(t) {
  const segs = PTS.length - 1;
  const tt = Math.max(0, Math.min(t, 1)) * segs;
  const i = Math.min(Math.floor(tt), segs - 1);
  const f = tt - i;
  const a = PTS[i], b = PTS[i + 1];
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, i, f };
}
function posAt(t) {
  const cur = sample(t);
  const ahead = sample(Math.min(t + 0.006, 1));
  const behind = sample(Math.max(t - 0.006, 0));
  const angle = (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI;
  const idx = cur.f < 0.5 ? cur.i : cur.i + 1;
  return { x: cur.x, y: cur.y, angle, idx };
}

const INIT = posAt(0);
const INIT_VBW = W * 0.34;

export default function PortsFlight() {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const svgRef = useRef(null);
  const eagleRef = useRef(null);
  const barRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hint, setHint] = useState(true);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    const setFrame = (t) => {
      const cont = stickyRef.current;
      const el = svgRef.current;
      if (!cont || !el) return;
      const r = cont.getBoundingClientRect();
      const vbW = W * 0.34;
      const vbH = vbW * (r.height / Math.max(r.width, 1));
      const p = posAt(t);
      el.setAttribute("viewBox", `${p.x - vbW / 2} ${p.y - vbH / 2} ${vbW} ${vbH}`);
      if (eagleRef.current) {
        eagleRef.current.setAttribute("transform", `translate(${p.x} ${p.y}) rotate(${p.angle + 90})`);
      }
      if (barRef.current) barRef.current.style.width = `${Math.round(t * 100)}%`;
      setActiveIdx((prev) => (prev === p.idx ? prev : p.idx));
      setHint((prev) => {
        const show = t < 0.03;
        return prev === show ? prev : show;
      });
    };
    setFrame(scrollYProgress.get());
    const unsub = scrollYProgress.on("change", setFrame);
    const onResize = () => setFrame(scrollYProgress.get());
    window.addEventListener("resize", onResize);
    return () => {
      unsub();
      window.removeEventListener("resize", onResize);
    };
  }, [scrollYProgress]);

  const active = PTS[activeIdx] || PTS[0];

  return (
    <section id="ports" ref={sectionRef} className="relative bg-arjuna-ink" style={{ height: "360vh" }} data-testid="ports-section">
      <div ref={stickyRef} className="sticky top-0 h-screen w-full overflow-hidden">
        {/* background accents */}
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-20" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(70% 60% at 50% 45%, rgba(0,229,255,0.08), transparent 70%)" }}
        />

        <svg
          ref={svgRef}
          className="absolute inset-0 h-full w-full"
          viewBox={`${INIT.x - INIT_VBW / 2} ${INIT.y - (INIT_VBW * 0.56) / 2} ${INIT_VBW} ${INIT_VBW * 0.56}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <radialGradient id="eagleGlowP" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(0,229,255,0.55)" />
              <stop offset="100%" stopColor="rgba(0,229,255,0)" />
            </radialGradient>
            <linearGradient id="flightGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#00E5FF" />
              <stop offset="100%" stopColor="#0044FF" />
            </linearGradient>
          </defs>

          {/* India landmass */}
          {STATE_PATHS.map((s) => (
            <path key={s.key} d={s.d} fill="rgba(15,23,42,0.85)" stroke="rgba(0,229,255,0.28)" strokeWidth="1.1" />
          ))}

          {/* flight path */}
          <path d={FLIGHT_D} fill="none" stroke="url(#flightGrad)" strokeWidth="2.5" strokeDasharray="6 8" opacity="0.55" strokeLinecap="round" />

          {/* port markers */}
          {PTS.map((p, i) => {
            const isActive = i === activeIdx;
            return (
              <g key={p.name}>
                {isActive && <circle cx={p.x} cy={p.y} r="16" fill="none" stroke="#00E5FF" strokeWidth="1.5" className="map-node-ring" />}
                <circle cx={p.x} cy={p.y} r={isActive ? 6 : 3.6} fill={isActive ? "#00E5FF" : "#38bdf8"} style={{ filter: isActive ? "drop-shadow(0 0 8px #00E5FF)" : "none" }} />
                <text x={p.x + 10} y={p.y - 8} fontSize="15" fontFamily="'JetBrains Mono', monospace" fill={isActive ? "#e0faff" : "rgba(148,163,184,0.7)"} style={{ letterSpacing: "0.5px" }}>
                  {p.name}
                </text>
              </g>
            );
          })}

          {/* eagle (camera follows it) */}
          <g ref={eagleRef} transform={`translate(${INIT.x} ${INIT.y}) rotate(${INIT.angle + 90})`}>
            <circle r={ES * 0.85} fill="url(#eagleGlowP)" className="animate-pulse" />
            <AMark x={-ES / 2} y={-ES / 2} width={ES} height={ES} fill="#ffffff" />
          </g>
        </svg>

        {/* HUD overlay */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-6 top-24 lg:left-10">
            <p className="overline">Ports of India</p>
            <h2 className="mt-3 max-w-md font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl">
              An eagle&apos;s-eye flight, east&nbsp;&rarr;&nbsp;west.
            </h2>
          </div>

          {/* current port card */}
          <div className="absolute bottom-16 left-6 lg:left-10" data-testid="ports-hud">
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-arjuna-teal">
                <Anchor className="h-3.5 w-3.5" /> Port {activeIdx + 1} / {PTS.length}
              </div>
              <p className="mt-2 font-heading text-2xl text-white" data-testid="ports-active-name">{active.name}</p>
              <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{active.region}</p>
            </div>
          </div>

          {/* scroll hint */}
          <div
            className="absolute bottom-16 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 transition-opacity duration-500"
            style={{ opacity: hint ? 1 : 0 }}
          >
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-300">
              <MousePointer2 className="h-4 w-4 text-arjuna-teal" /> Scroll to fly
            </div>
            <ChevronDown className="h-5 w-5 animate-bounce text-arjuna-teal" />
          </div>

          {/* progress bar */}
          <div className="absolute inset-x-0 bottom-0 h-1 bg-white/5">
            <div ref={barRef} className="h-full bg-gradient-to-r from-arjuna-teal to-arjuna-blue" style={{ width: "0%" }} />
          </div>
        </div>
      </div>
    </section>
  );
}
